import { useCallback, useState } from 'react';

import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import {
  faCheck,
  faEllipsis,
  faGripLines,
  faGripLinesVertical,
  faPlus,
  faTableCellsColumns,
  faTableCellsHeader,
  faTableColumnsAddAfter,
  faTableColumnsAddBefore,
  faTableRowsAddAbove,
  faTableRowsAddBelow,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const HANDLE_SIZE = 14;
export const HANDLE_GAP = 2;
export const HANDLE_OFFSET = HANDLE_SIZE + HANDLE_GAP;
export const ADD_BUTTON_SIZE = 18;
export const ADD_BUTTON_OFFSET = ADD_BUTTON_SIZE + HANDLE_GAP;
const RESIZE_GRIP_WIDTH = 9;

export const HANDLE_CLASSNAME = 'tableHandle';
const OPEN_CLASSNAME = 'tableHandle_open';

// The handles live outside the editable flow, so every pointer interaction has to suppress the
// default mousedown behaviour or the browser pulls the selection out of the active cell.
function preventDefault(e) {
  e.preventDefault();
}

function useMenu(onToggleMenu) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = useCallback(
    e => {
      e.preventDefault();
      setAnchorEl(e.currentTarget);
      onToggleMenu?.(true);
    },
    [onToggleMenu],
  );
  const close = useCallback(() => {
    setAnchorEl(null);
    onToggleMenu?.(false);
  }, [onToggleMenu]);

  return { anchorEl, className: anchorEl ? `${HANDLE_CLASSNAME} ${OPEN_CLASSNAME}` : HANDLE_CLASSNAME, close, open };
}

function HandleMenu({ anchorEl, children, onClose }) {
  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={ANCHOR_ORIGIN}
      css={menuCss}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      onClose={onClose}
      open={Boolean(anchorEl)}
      slotProps={{ list: { dense: true, onMouseDown: preventDefault } }}
      transformOrigin={TRANSFORM_ORIGIN}
    >
      {children}
    </Menu>
  );
}

function ActionItem({ icon, label, onSelect, selected }) {
  return (
    <MenuItem
      onMouseDown={e => {
        e.preventDefault();
        onSelect();
      }}
    >
      <ListItemIcon>
        <FontAwesomeIcon icon={icon} />
      </ListItemIcon>
      <ListItemText>{label}</ListItemText>
      {selected && <FontAwesomeIcon icon={faCheck} size="xs" />}
    </MenuItem>
  );
}

export function ColumnHandle({ canDelete, columnIndex, onDelete, onInsert, onStartResize, onToggleMenu }) {
  const { anchorEl, className, close, open } = useMenu(onToggleMenu);

  return (
    <>
      <div className={className} contentEditable={false} css={columnHandleCss} onMouseDown={open}>
        <FontAwesomeIcon icon={faGripLines} size="xs" />
      </div>
      <div contentEditable={false} css={resizeGripCss} onMouseDown={e => onStartResize(e, columnIndex)} />
      <HandleMenu anchorEl={anchorEl} onClose={close}>
        <ActionItem
          icon={faTableColumnsAddBefore}
          label="Insert column left"
          onSelect={() => {
            close();
            onInsert(columnIndex);
          }}
        />
        <ActionItem
          icon={faTableColumnsAddAfter}
          label="Insert column right"
          onSelect={() => {
            close();
            onInsert(columnIndex + 1);
          }}
        />
        {canDelete && (
          <ActionItem
            icon={faTrash}
            label="Delete column"
            onSelect={() => {
              close();
              onDelete(columnIndex);
            }}
          />
        )}
      </HandleMenu>
    </>
  );
}

export function RowHandle({ canDelete, onDelete, onInsert, onToggleMenu, rowIndex }) {
  const { anchorEl, className, close, open } = useMenu(onToggleMenu);

  return (
    <>
      <div className={className} contentEditable={false} css={rowHandleCss} onMouseDown={open}>
        <FontAwesomeIcon icon={faGripLinesVertical} size="xs" />
      </div>
      <HandleMenu anchorEl={anchorEl} onClose={close}>
        <ActionItem
          icon={faTableRowsAddAbove}
          label="Insert row above"
          onSelect={() => {
            close();
            onInsert(rowIndex);
          }}
        />
        <ActionItem
          icon={faTableRowsAddBelow}
          label="Insert row below"
          onSelect={() => {
            close();
            onInsert(rowIndex + 1);
          }}
        />
        {canDelete && (
          <ActionItem
            icon={faTrash}
            label="Delete row"
            onSelect={() => {
              close();
              onDelete(rowIndex);
            }}
          />
        )}
      </HandleMenu>
    </>
  );
}

export function TableOptionsHandle({
  headerColumn,
  headerRow,
  onDeleteTable,
  onToggleHeaderColumn,
  onToggleHeaderRow,
  onToggleMenu,
}) {
  const { anchorEl, className, close, open } = useMenu(onToggleMenu);

  return (
    <>
      <Tooltip enterDelay={500} placement="top" title="table options">
        <div className={className} contentEditable={false} css={optionsHandleCss} onMouseDown={open}>
          <FontAwesomeIcon icon={faEllipsis} size="xs" />
        </div>
      </Tooltip>
      <HandleMenu anchorEl={anchorEl} onClose={close}>
        <ActionItem icon={faTableCellsHeader} label="Header row" onSelect={onToggleHeaderRow} selected={headerRow} />
        <ActionItem
          icon={faTableCellsColumns}
          label="Header column"
          onSelect={onToggleHeaderColumn}
          selected={headerColumn}
        />
        <ActionItem
          icon={faTrash}
          label="Delete table"
          onSelect={() => {
            close();
            onDeleteTable();
          }}
        />
      </HandleMenu>
    </>
  );
}

export function AddButton({ onAdd, orientation, tooltip }) {
  return (
    <Tooltip enterDelay={500} placement={orientation === 'column' ? 'right' : 'bottom'} title={tooltip}>
      <div
        className={HANDLE_CLASSNAME}
        contentEditable={false}
        css={orientation === 'column' ? addColumnButtonCss : addRowButtonCss}
        onMouseDown={e => {
          e.preventDefault();
          onAdd();
        }}
      >
        <FontAwesomeIcon icon={faPlus} size="xs" />
      </div>
    </Tooltip>
  );
}

const ANCHOR_ORIGIN = { horizontal: 'left', vertical: 'bottom' };
const TRANSFORM_ORIGIN = { horizontal: 'left', vertical: 'top' };

const menuCss = ({ zIndex }) => ({ zIndex: zIndex.modal + 2 }); // 1 above the editor toolbar

const handleBaseCss = ({ palette, transitions }) => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9,
  color: palette.text.secondary,
  backgroundColor: palette.action.hover,
  borderRadius: 3,
  cursor: 'pointer',
  opacity: 0,
  transition: transitions.create('opacity'),
  userSelect: 'none',

  [`&:hover, &.${OPEN_CLASSNAME}`]: { backgroundColor: palette.action.selected, opacity: 1 },
});

const columnHandleCss = theme => ({
  ...handleBaseCss(theme),
  top: -HANDLE_OFFSET,
  left: 0,
  right: 0,
  height: HANDLE_SIZE,
});

const rowHandleCss = theme => ({
  ...handleBaseCss(theme),
  left: -HANDLE_OFFSET,
  top: 0,
  bottom: 0,
  width: HANDLE_SIZE,
});

const optionsHandleCss = theme => ({
  ...handleBaseCss(theme),
  top: -HANDLE_OFFSET,
  left: -HANDLE_OFFSET,
  width: HANDLE_SIZE,
  height: HANDLE_SIZE,
});

// Intentionally not tagged with HANDLE_CLASSNAME: the grip stays invisible until pointed at, so the
// table doesn't sprout a blue line on every column boundary the moment it is hovered.
const resizeGripCss = ({ palette, transitions }) => ({
  position: 'absolute',
  top: -HANDLE_OFFSET,
  right: -Math.floor(RESIZE_GRIP_WIDTH / 2),
  width: RESIZE_GRIP_WIDTH,
  height: HANDLE_SIZE,
  cursor: 'col-resize',
  opacity: 0,
  transition: transitions.create('opacity'),
  zIndex: 1,

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '50%',
    width: 2,
    marginLeft: -1,
    borderRadius: 1,
    backgroundColor: palette.primary.main,
  },
  '&:hover': { opacity: 1 },
});

const addColumnButtonCss = theme => ({
  ...handleBaseCss(theme),
  top: 0,
  bottom: 0,
  right: -ADD_BUTTON_OFFSET,
  width: ADD_BUTTON_SIZE,
});

const addRowButtonCss = theme => ({
  ...handleBaseCss(theme),
  left: 0,
  right: 0,
  bottom: -ADD_BUTTON_OFFSET,
  height: ADD_BUTTON_SIZE,
});
